import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError, UnexpectedResponseError, onStaleVersion } from '@/shared/api/http';
import { getSession, refreshSession, subscribeSession } from '@/shared/auth';
import { Toaster } from '@/shared/ui/Toast';

/**
 * Создаёт клиент запросов с общими для всей админки правилами.
 *
 * Клиент создаётся внутри компонента, а не в модуле: модульный экземпляр
 * пережил бы горячую перезагрузку и утащил в неё устаревший кеш.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Данные админки меняются от действий пользователя, а не сами по себе,
        // поэтому перезапрос при каждом возврате в окно только мешает.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Повторять запрос, на который сервер осознанно ответил «нельзя» или
          // «не найдено», бессмысленно: ответ не изменится, а пользователь
          // будет ждать. Повторяем только сетевые сбои и ошибки сервера.
          if (error instanceof ApiError && error.status < 500) {
            return false;
          }

          // Запрос ушёл не по адресу — повторы это не исправят, адрес нужно
          // чинить в настройках окружения.
          if (error instanceof UnexpectedResponseError) {
            return false;
          }

          return failureCount < 2;
        },
      },
    },
  });
}

/**
 * Кто сейчас в сеансе — с точностью, достаточной, чтобы заметить подмену.
 *
 * Работа от чужого имени учитывается отдельно от самого человека: владелец
 * платформы, вошедший под пользователя и вернувшийся обратно, — это два
 * разных набора прав, хотя идентификатор в обоих случаях может совпасть.
 */
function currentUserID(): string {
  const session = getSession();

  return session.status === 'authenticated'
    ? `${session.user.id}:${String(session.impersonated)}`
    : session.status;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  // Токен доступа живёт в памяти вкладки и умирает вместе с ней, а вот cookie
  // с токеном обновления переживает перезагрузку. Поэтому при запуске
  // приложение один раз пробует восстановить сеанс: без этого F5 выкидывал бы
  // из системы, хотя войти человек не переставал.
  useEffect(() => {
    void refreshSession();
  }, []);

  // Смена того, кто вошёл, стирает кеш запросов целиком.
  //
  // Без этого следующий человек несколько секунд смотрит на строки
  // предыдущего: данные из кеша считаются свежими (staleTime), поэтому
  // рисуются сразу, а перезапрос приходит позже. На экране это неотличимо от
  // утечки чужих данных — и на общем компьютере ею и является.
  //
  // Сюда же попадают вход от чужого имени и возврат обратно: там меняется
  // ровно то же самое — чьи данные вправе показывать приложение.
  //
  // Кроме первого выяснения сеанса при запуске («неизвестно» → кто-то). Это
  // не смена человека: чужих данных в кеше ещё нет, а стирание выбрасывало
  // запросы, которые экран уже отправил, — приглашение по ссылке навсегда
  // застревало на «Проверяем приглашение…».
  useEffect(() => {
    let previous = currentUserID();

    return subscribeSession(() => {
      const next = currentUserID();

      if (next === previous) {
        return;
      }

      const restoring = previous === 'unknown';
      previous = next;

      if (!restoring) {
        queryClient.clear();
      }
    });
  }, [queryClient]);

  // Правку отклонили, потому что объект уже изменили: всё на экране
  // перечитывается, чтобы человек увидел чужую правку, а следующее
  // сохранение ушло с новой версией. Набранное в формах не теряется — формы
  // держат его в своём состоянии.
  useEffect(
    () =>
      onStaleVersion(() => {
        void queryClient.invalidateQueries();
      }),
    [queryClient],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {/* Показ тостов живёт рядом с провайдерами, а не внутри каркаса
          страницы: об ошибке запроса надо сообщить и на экране входа, и на
          странице, которая упала в обработчик ошибок, — то есть там, где
          каркаса ещё или уже нет. */}
      <Toaster />
    </QueryClientProvider>
  );
}
